import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';

import {Headline, P} from 'UI/components/layout/Text';
import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';

import {useHeroBlock} from './useHeroBlock';

import type {Media as MediaType} from 'types/payload-types';

/** Available hero size variants. */
export type HeroSize = 'large' | 'medium' | 'small';

/** Height mapping for each hero size. */
const SIZE_HEIGHTS: Record<HeroSize, string> = {
    large: '360px',
    medium: '240px',
    small: '160px'
};

/**
 * Props for the HeroBlock component.
 */
interface ComponentProps {
    /** The background image (populated Media document or ID). */
    backgroundImage?: MediaType | string | null;
    /** The main headline text. */
    headline: string;
    /** The display size of the hero. Defaults to `'large'`. */
    size?: HeroSize;
    /** The subline/subtitle text. */
    subline?: string | null;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     *
     * @default 'HeroBlock'
     */
    testId?: string;
}

/**
 * Renders a hero banner with background image, headline and subline.
 * Supports three sizes: `large`, `medium`, and `small`.
 *
 * @param props                 The component props.
 * @param props.backgroundImage The hero background image.
 * @param props.headline        The main headline text.
 * @param props.size            The hero display size. Defaults to `'large'`.
 * @param props.subline         The subtitle text.
 * @param props.testId          An optional test ID. Defaults to `'HeroBlock'`.
 * @returns A JSX element rendering the hero block.
 *
 * @example
 * ```tsx
 * <HeroBlock headline="Entdecke Augsburg" subline="Audio-geführte Spaziergänge" size="large" />
 * ```
 */
const HeroBlock = ({backgroundImage, headline, size = 'large', subline, testId = 'HeroBlock'}: ComponentProps) => {
    const {imageUrl, imgData, media: mediaDoc} = useHeroBlock(backgroundImage, size);

    return (
        <Wrapper $size={size} data-cy={testId}>
            {imageUrl && (
                <ImageWrapper $size={size}>
                    <ResponsiveImage
                        alt={mediaDoc?.alt ?? ''}
                        focalX={imgData?.focalX}
                        focalY={imgData?.focalY}
                        sizes="100vw"
                        src={imageUrl}
                        srcSet={imgData?.srcSet}
                        testId={`${testId}-image`}
                    />
                </ImageWrapper>
            )}
            <Overlay $size={size}>
                <HeroHeadline $size="headline" as="h1">{headline}</HeroHeadline>
                {subline && size !== 'small' && <HeroSubline $size="body">{subline}</HeroSubline>}
            </Overlay>
        </Wrapper>
    );
};

HeroBlock.displayName = 'HeroBlock';

export {HeroBlock};

// ── Styled ──────────────────────────────────────────────────

interface SizeProps {
    $size: HeroSize;
}

const Wrapper = styled.section<SizeProps>`
    position: relative;
    width: 100%;
`;

const ImageWrapper = styled.div<SizeProps>`
    border-radius: 16px;
    height: ${({$size}) => SIZE_HEIGHTS[$size]};
    overflow: hidden;
    width: 100%;

    ${media('lg')} {
        height: auto;
    }
`;

const Overlay = styled.div<SizeProps>`
    backdrop-filter: blur(10px);
    background: #ffffffbd;
    border-radius: 8px;
    border-radius: ${spacing(5)};
    box-shadow: ${({theme}) => theme.boxShadows.cardShadow};
    display: flex;
    flex-direction: column;
    gap: ${spacing(3)};
    justify-content: flex-end;
    margin: -2rem ${spacing(2)} ${spacing(2)};
    padding: ${spacing(4)};


    ${media('lg')} {
        border-radius: 16px;
        bottom: 0;
        margin: 0;
        padding: ${spacing(8)};
        position: absolute;
    }
`;

const HeroHeadline = styled(Headline)`
    color: ${({theme}) => theme.colors.primaryDefault};
`;

const HeroSubline = styled(P)`
    color: ${({theme}) => theme.colors.primaryDefault};
`;